import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
    LayoutDashboard, Users, Activity, Calendar, Inbox, Settings, Search,
    UserCheck, BarChart3, Heart, ShoppingCart, Package, Video, Play, X,
    Stethoscope, CheckCircle2, XCircle, Clock, Radio, KeyRound, RefreshCw, Copy,
    UserCog, FileImage, ScanLine, Users2,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { DashSidebar } from "../components/DashSidebar";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { AdminRecordingsTab } from "../components/AdminRecordingsTab";

const ITEMS = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
    { id: "patients", label: "Patients", icon: Users },
    { id: "doctor-approvals", label: "Doctor Approvals", icon: Stethoscope },
    { id: "profile-changes", label: "Profile Changes", icon: UserCog },
    { id: "scan-reviews", label: "Scan Reviews", icon: ScanLine },
    { id: "dependents", label: "Dependents", icon: Users2 },
    { id: "sessions", label: "Kiosk Sessions", icon: Activity },
    { id: "kiosk-devices", label: "Kiosk Devices", icon: Radio },
    { id: "orders", label: "Orders", icon: ShoppingCart },
    { id: "recordings", label: "Video Recordings", icon: Video },
    { id: "webinar", label: "Webinar Registrations", icon: Calendar },
    { id: "inbox", label: "Inbox", icon: Inbox },
    { id: "settings", label: "Settings", icon: Settings },
];

function Kpi({ icon: Icon, label, value, accent = "var(--medx-red)" }) {
    return (
        <div className="card-soft p-6 relative overflow-hidden" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="absolute -right-6 -top-6 w-20 h-20 rounded-full opacity-10" style={{ background: accent }} />
            <Icon className="w-6 h-6 mb-3" style={{ color: accent }} />
            <div className="text-xs font-mono tracking-[0.22em] text-gray-500 uppercase">{label}</div>
            <div className="font-display text-4xl font-bold mt-1 text-gray-900 tabular-nums">{value}</div>
        </div>
    );
}

export default function AdminDashboard() {
    const { user } = useAuth();
    const { hash } = useLocation();
    const tab = (hash || "#overview").replace("#", "");

    const [stats, setStats] = useState(null);
    const [patients, setPatients] = useState([]);
    const [sessions, setSessions] = useState([]);
    const [regs, setRegs] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [orders, setOrders] = useState([]);
    const [search, setSearch] = useState("");

    const loadAll = async () => {
        try {
            const [s, p, ks, w, c, o] = await Promise.all([
                api.get("/admin/stats"),
                api.get("/admin/patients"),
                api.get("/admin/sessions"),
                api.get("/admin/webinar-registrations"),
                api.get("/admin/contacts"),
                api.get("/admin/orders"),
            ]);
            setStats(s.data); setPatients(p.data); setSessions(ks.data); setRegs(w.data); setContacts(c.data); setOrders(o.data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to load admin data");
        }
    };
    useEffect(() => { loadAll(); }, []);

    const filteredPatients = useMemo(() => {
        const q = search.toLowerCase();
        return patients.filter((p) => !q || p.name?.toLowerCase().includes(q) || p.email?.toLowerCase().includes(q));
    }, [patients, search]);

    const sessionsByDay = useMemo(() => {
        const buckets = {};
        sessions.forEach((s) => {
            const d = new Date(s.vitals.captured_at);
            const k = d.toLocaleDateString();
            buckets[k] = (buckets[k] || 0) + 1;
        });
        return Object.entries(buckets).slice(-10).map(([day, count]) => ({ day, count }));
    }, [sessions]);

    return (
        <div className="flex bg-[var(--medx-bg-soft)] min-h-screen" data-testid="admin-dashboard">
            <DashSidebar items={ITEMS} basePath="/admin" title="Admin" />

            <main className="flex-1 p-6 md:p-10">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                    <div>
                        <div className="text-xs font-mono tracking-[0.22em] text-[var(--medx-red)] mb-1">ADMIN CONSOLE</div>
                        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
                            Welcome, {user?.name?.split(" ")[0]}.
                        </h1>
                    </div>
                    <button onClick={loadAll} className="btn-outline !py-2 !px-4 text-sm" data-testid="admin-refresh">
                        Refresh data
                    </button>
                </div>

                {tab === "overview" && stats && (
                    <div className="space-y-8" data-testid="admin-tab-overview">
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                            <Kpi icon={Users} label="Patients" value={stats.total_patients} />
                            <Kpi icon={Activity} label="Total Scans" value={stats.total_sessions} accent="var(--medx-orange)" />
                            <Kpi icon={UserCheck} label="Sessions Today" value={stats.sessions_today} />
                            <Kpi icon={Heart} label="Avg Score" value={stats.avg_health_score} accent="#16a34a" />
                            <Kpi icon={Calendar} label="Webinar Regs" value={stats.webinar_registrations} accent="var(--medx-orange)" />
                            <Kpi icon={Inbox} label="Inbox" value={stats.contact_messages} accent="#0ea5e9" />
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            <div className="lg:col-span-2 card-soft p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <div>
                                        <div className="text-xs font-mono tracking-[0.22em] text-gray-500">DAILY SCANS</div>
                                        <div className="font-display text-xl font-semibold">Last 10 days</div>
                                    </div>
                                    <BarChart3 className="w-5 h-5 text-[var(--medx-orange)]" />
                                </div>
                                <div className="h-64">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={sessionsByDay}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                                            <XAxis dataKey="day" stroke="#9ca3af" fontSize={11} />
                                            <YAxis stroke="#9ca3af" fontSize={11} />
                                            <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #f3f4f6" }} />
                                            <Bar dataKey="count" fill="#e63946" radius={[8, 8, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            <div className="card-soft p-6">
                                <div className="text-xs font-mono tracking-[0.22em] text-gray-500 mb-3">RECENT ACTIVITY</div>
                                <div className="space-y-3 max-h-64 overflow-auto">
                                    {sessions.slice(0, 6).map((s) => (
                                        <div key={s.id} className="flex items-start gap-3 pb-3 border-b border-gray-100 last:border-0">
                                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[var(--medx-red)] to-[var(--medx-orange)] grid place-items-center text-white text-xs font-bold">
                                                {(s.user_name || "U").slice(0, 1).toUpperCase()}
                                            </div>
                                            <div className="text-sm flex-1">
                                                <div className="font-medium text-gray-900">{s.user_name}</div>
                                                <div className="text-xs text-gray-500">Score {s.vitals.health_score} · {s.vitals.kiosk_id}</div>
                                            </div>
                                            <div className="text-xs text-gray-400">{new Date(s.vitals.captured_at).toLocaleDateString()}</div>
                                        </div>
                                    ))}
                                    {sessions.length === 0 && <div className="text-sm text-gray-500 text-center py-6">No kiosk activity yet.</div>}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {tab === "patients" && (
                    <div className="space-y-6" data-testid="admin-tab-patients">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                            <h2 className="font-display text-2xl font-semibold">Patients</h2>
                            <div className="relative max-w-sm w-full">
                                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text" placeholder="Search by name or email"
                                    value={search} onChange={(e) => setSearch(e.target.value)}
                                    className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm"
                                    data-testid="admin-patient-search"
                                />
                            </div>
                        </div>
                        <div className="card-soft overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 text-gray-500 text-xs font-mono tracking-wider">
                                    <tr>
                                        <th className="text-left p-4">PATIENT</th>
                                        <th className="text-left p-4">EMAIL</th>
                                        <th className="text-left p-4">PHONE</th>
                                        <th className="text-left p-4">SCANS</th>
                                        <th className="text-left p-4">SCORE</th>
                                        <th className="text-left p-4">JOINED</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredPatients.length === 0 && (
                                        <tr><td colSpan={6} className="p-8 text-center text-gray-500">No patients yet.</td></tr>
                                    )}
                                    {filteredPatients.map((p) => (
                                        <tr key={p.id} onClick={() => window.location.assign(`/admin/patients/${p.id}`)} className="border-t border-gray-100 hover:bg-orange-50/40 cursor-pointer" data-testid={`admin-patient-row-${p.id}`}>
                                            <td className="p-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[var(--medx-red)] to-[var(--medx-orange)] grid place-items-center text-white text-xs font-bold">
                                                        {(p.name || "U").slice(0, 1).toUpperCase()}
                                                    </div>
                                                    <div className="font-medium text-gray-900">{p.name}</div>
                                                </div>
                                            </td>
                                            <td className="p-4 text-gray-600">{p.email}</td>
                                            <td className="p-4 text-gray-600">{p.phone || "—"}</td>
                                            <td className="p-4 font-bold">{p.session_count}</td>
                                            <td className="p-4 font-bold text-[var(--medx-red)]">{p.health_score || 0}</td>
                                            <td className="p-4 text-gray-500 text-xs">{new Date(p.created_at).toLocaleDateString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {tab === "sessions" && (
                    <div className="space-y-6" data-testid="admin-tab-sessions">
                        <h2 className="font-display text-2xl font-semibold">Kiosk Sessions</h2>
                        <div className="card-soft overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 text-gray-500 text-xs font-mono tracking-wider">
                                    <tr>
                                        <th className="text-left p-4">PATIENT</th>
                                        <th className="text-left p-4">KIOSK</th>
                                        <th className="text-left p-4">BP</th>
                                        <th className="text-left p-4">HR</th>
                                        <th className="text-left p-4">SpO₂</th>
                                        <th className="text-left p-4">TEMP</th>
                                        <th className="text-left p-4">RESP</th>
                                        <th className="text-left p-4">SCORE</th>
                                        <th className="text-left p-4">DATE</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sessions.length === 0 && (
                                        <tr><td colSpan={8} className="p-8 text-center text-gray-500">No kiosk sessions yet.</td></tr>
                                    )}
                                    {sessions.map((s) => (
                                        <tr key={s.id} className="border-t border-gray-100 hover:bg-orange-50/40">
                                            <td className="p-4">
                                                <div className="font-medium text-gray-900">{s.user_name}</div>
                                                <div className="text-xs text-gray-500">{s.user_email}</div>
                                            </td>
                                            <td className="p-4 font-mono text-xs">{s.vitals.kiosk_id}</td>
                                            <td className="p-4">{s.vitals.blood_pressure_systolic}/{s.vitals.blood_pressure_diastolic}</td>
                                            <td className="p-4">{s.vitals.heart_rate}</td>
                                            <td className="p-4">{s.vitals.spo2}%</td>
                                            <td className="p-4">{s.vitals.body_temperature}°C</td>
                                            <td className="p-4">{s.vitals.respiratory_rate}/min</td>
                                            <td className="p-4 font-bold text-[var(--medx-red)]">{s.vitals.health_score}</td>
                                            <td className="p-4 text-xs text-gray-500">{new Date(s.vitals.captured_at).toLocaleString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {tab === "orders" && (
                    <div className="space-y-6" data-testid="admin-tab-orders">
                        <h2 className="font-display text-2xl font-semibold">Orders & Bookings</h2>
                        <div className="card-soft overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 text-gray-500 text-xs font-mono tracking-wider">
                                    <tr>
                                        <th className="text-left p-4">COMPANY</th>
                                        <th className="text-left p-4">TYPE</th>
                                        <th className="text-left p-4">UNITS</th>
                                        <th className="text-left p-4">EVENT</th>
                                        <th className="text-left p-4">EVENT DATE</th>
                                        <th className="text-left p-4">CONTACT</th>
                                        <th className="text-left p-4">RECEIVED</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {orders.length === 0 && (
                                        <tr><td colSpan={7} className="p-8 text-center text-gray-500">No orders yet.</td></tr>
                                    )}
                                    {orders.map((o) => (
                                        <tr key={o.id} className="border-t border-gray-100 hover:bg-orange-50/40">
                                            <td className="p-4">
                                                <div className="font-medium text-gray-900">{o.company_name}</div>
                                                <div className="text-xs text-gray-500">{o.address}</div>
                                            </td>
                                            <td className="p-4">
                                                <span className={`text-xs px-2 py-1 rounded-full ${o.order_type === "buy" ? "bg-red-50 text-[var(--medx-red)]" : "bg-orange-50 text-[var(--medx-orange)]"}`}>
                                                    {o.order_type === "buy" ? "Buy" : "Rent"}
                                                </span>
                                            </td>
                                            <td className="p-4 font-bold">{o.units}</td>
                                            <td className="p-4 text-gray-700">{o.event_name || "—"}</td>
                                            <td className="p-4 text-gray-700">{o.event_date ? new Date(o.event_date).toLocaleDateString() : "—"}</td>
                                            <td className="p-4 text-xs">
                                                <div className="text-gray-900 font-medium">{o.email}</div>
                                                <div className="text-gray-500">{o.telephone}</div>
                                            </td>
                                            <td className="p-4 text-xs text-gray-500">{new Date(o.created_at).toLocaleString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {tab === "recordings" && <AdminRecordingsTab />}
                {tab === "doctor-approvals" && <DoctorApprovalsTab />}
                {tab === "profile-changes" && <ProfileChangesTab />}
                {tab === "scan-reviews" && <ScanReviewsTab />}
                {tab === "dependents" && <DependentsTab />}
                {tab === "kiosk-devices" && <KioskDevicesTab />}

                {tab === "webinar" && (
                    <div className="space-y-6" data-testid="admin-tab-webinar">
                        <h2 className="font-display text-2xl font-semibold">Webinar Registrations</h2>
                        <div className="card-soft overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 text-gray-500 text-xs font-mono tracking-wider">
                                    <tr>
                                        <th className="text-left p-4">NAME</th>
                                        <th className="text-left p-4">EMAIL</th>
                                        <th className="text-left p-4">ROLE</th>
                                        <th className="text-left p-4">ORGANIZATION</th>
                                        <th className="text-left p-4">COUNTRY</th>
                                        <th className="text-left p-4">REGISTERED</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {regs.length === 0 && (
                                        <tr><td colSpan={6} className="p-8 text-center text-gray-500">No registrations yet.</td></tr>
                                    )}
                                    {regs.map((r) => (
                                        <tr key={r.id} className="border-t border-gray-100 hover:bg-orange-50/40">
                                            <td className="p-4 font-medium">{r.full_name}</td>
                                            <td className="p-4 text-gray-600">{r.email}</td>
                                            <td className="p-4"><span className="text-xs px-2 py-1 rounded-full bg-orange-50 text-[var(--medx-red)]">{r.role}</span></td>
                                            <td className="p-4 text-gray-600">{r.organization || "—"}</td>
                                            <td className="p-4 text-gray-600">{r.country || "—"}</td>
                                            <td className="p-4 text-xs text-gray-500">{new Date(r.created_at).toLocaleString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {tab === "inbox" && (
                    <div className="space-y-6" data-testid="admin-tab-inbox">
                        <h2 className="font-display text-2xl font-semibold">Contact Inbox</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            {contacts.length === 0 && (
                                <div className="card-soft p-8 text-center text-gray-500 md:col-span-2">No messages yet.</div>
                            )}
                            {contacts.map((c) => (
                                <div key={c.id} className="card-soft p-6">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="font-display font-semibold text-gray-900">{c.subject}</div>
                                        <div className="text-xs text-gray-400">{new Date(c.created_at).toLocaleDateString()}</div>
                                    </div>
                                    <div className="text-sm text-gray-500 mb-3">From <span className="font-medium text-gray-800">{c.name}</span> · {c.email}</div>
                                    <p className="text-sm text-gray-700 leading-relaxed">{c.message}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {tab === "settings" && (
                    <div className="space-y-6" data-testid="admin-tab-settings">
                        <h2 className="font-display text-2xl font-semibold">Settings</h2>
                        <div className="card-soft p-7">
                            <div className="text-sm text-gray-600">Admin: <span className="font-semibold">{user?.email}</span></div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}


// =================================================================
// Doctor Approvals tab (Iteration 10)
// =================================================================
function DoctorApprovalsTab() {
    const [filter, setFilter] = useState("pending");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null); // id currently being decided

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/doctor-approvals?status_filter=${filter}`);
            setRows(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not load applications");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [filter]); // eslint-disable-line

    const decide = async (id, action) => {
        setBusy(id);
        // Optimistic UI: when on the Pending filter, remove the row instantly so the
        // list reflects the state change immediately (prevents the row from briefly
        // reappearing on the subsequent reload).
        const prevRows = rows;
        if (filter === "pending") setRows((r) => r.filter((x) => x.id !== id));
        try {
            await api.post(`/doctors/${id}/${action}`);
            toast.success(action === "approve" ? "Doctor approved. Confirmation email sent." : "Application rejected.");
            await load();
        } catch (e) {
            // Roll back optimistic removal on failure
            setRows(prevRows);
            toast.error(formatApiError(e.response?.data?.detail) || `Could not ${action}`);
        } finally {
            setBusy(null);
        }
    };

    const StatusPill = ({ s }) => {
        const map = {
            pending: { Icon: Clock, cls: "bg-amber-50 text-amber-700 border-amber-200" },
            approved: { Icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
            rejected: { Icon: XCircle, cls: "bg-rose-50 text-rose-700 border-rose-200" },
        };
        const { Icon, cls } = map[s] || map.pending;
        return (
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${cls}`}>
                <Icon className="w-3.5 h-3.5" /> {s}
            </span>
        );
    };

    return (
        <section data-testid="doctor-approvals-tab">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <div>
                    <h2 className="font-display text-2xl font-semibold">Doctor Applications</h2>
                    <p className="text-sm text-gray-500 mt-1">Review new doctor registrations. Doctors cannot log in until approved.</p>
                </div>
                <div className="inline-flex items-center gap-1 rounded-full bg-gray-100 p-1 text-xs">
                    {["pending", "approved", "rejected", "all"].map((f) => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            className={`px-3 py-1.5 rounded-full font-medium capitalize ${filter === f ? "bg-white shadow text-[var(--medx-red)]" : "text-gray-600 hover:text-gray-900"}`}
                            data-testid={`doctor-approvals-filter-${f}`}
                        >
                            {f}
                        </button>
                    ))}
                </div>
            </div>

            {loading && <div className="text-sm text-gray-500">Loading…</div>}
            {!loading && rows.length === 0 && (
                <div className="card-soft p-10 text-center text-sm text-gray-500">
                    No <span className="font-semibold">{filter}</span> applications right now.
                </div>
            )}
            {!loading && rows.length > 0 && (
                <div className="card-soft overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-orange-50/60 text-xs font-mono tracking-[0.18em] text-gray-500">
                            <tr>
                                <th className="text-left p-4">DOCTOR</th>
                                <th className="text-left p-4">SPECIALITY</th>
                                <th className="text-left p-4">LICENSE</th>
                                <th className="text-left p-4">APPLIED</th>
                                <th className="text-left p-4">STATUS</th>
                                <th className="text-right p-4">ACTION</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((d) => (
                                <tr key={d.id} className="border-t border-gray-100 hover:bg-orange-50/30" data-testid={`doctor-approval-row-${d.id}`}>
                                    <td className="p-4">
                                        <div className="font-semibold">{d.name}</div>
                                        <div className="text-xs text-gray-500 font-mono">{d.email}</div>
                                    </td>
                                    <td className="p-4 text-gray-700">{d.speciality || "—"}</td>
                                    <td className="p-4 text-gray-700 font-mono text-xs">{d.license_number || "—"}</td>
                                    <td className="p-4 text-gray-700">{new Date(d.created_at).toLocaleDateString()}</td>
                                    <td className="p-4"><StatusPill s={d.approval_status || "pending"} /></td>
                                    <td className="p-4 text-right">
                                        {d.approval_status === "pending" ? (
                                            <div className="inline-flex gap-2">
                                                <button
                                                    onClick={() => decide(d.id, "approve")}
                                                    disabled={busy === d.id}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50"
                                                    data-testid={`doctor-approval-approve-${d.id}`}
                                                >
                                                    <CheckCircle2 className="w-3.5 h-3.5" /> {busy === d.id ? "…" : "Approve"}
                                                </button>
                                                <button
                                                    onClick={() => { if (window.confirm(`Reject ${d.name}?`)) decide(d.id, "reject"); }}
                                                    disabled={busy === d.id}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-rose-500 text-white hover:bg-rose-600 disabled:opacity-50"
                                                    data-testid={`doctor-approval-reject-${d.id}`}
                                                >
                                                    <XCircle className="w-3.5 h-3.5" /> Reject
                                                </button>
                                            </div>
                                        ) : (
                                            <span className="text-xs text-gray-400 italic">Decided</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
}


function KioskDevicesTab() {
    const [keyInfo, setKeyInfo] = useState(null);
    const [reveal, setReveal] = useState(false);
    const [pushes, setPushes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [rotating, setRotating] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [k, p] = await Promise.all([
                api.get("/admin/kiosk/key"),
                api.get("/admin/kiosk/pushes?limit=50"),
            ]);
            setKeyInfo(k.data);
            setPushes(p.data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not load kiosk settings");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 15000);
        return () => clearInterval(t);
    }, []);

    const rotate = async () => {
        if (!window.confirm("Rotate the kiosk API key? The current key will stop working immediately — you'll need to update every physical kiosk device with the new key.")) return;
        setRotating(true);
        try {
            const { data } = await api.post("/admin/kiosk/key/rotate");
            setKeyInfo(data);
            setReveal(true);
            toast.success("New key generated. Copy it now and update your devices.");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Rotation failed");
        } finally {
            setRotating(false);
        }
    };

    const copy = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success("Copied to clipboard");
        } catch {
            toast.error("Copy failed — select & copy manually");
        }
    };

    const apiOrigin = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const curlSnippet = `curl -X POST "${apiOrigin}/api/kiosk/push" \\
  -H "Content-Type: application/json" \\
  -H "X-Kiosk-Key: ${keyInfo?.api_key || "YOUR_KIOSK_API_KEY"}" \\
  -d '{
    "patient_email": "patient@medxai.co.za",
    "kiosk_id": "MEDX-JHB-001",
    "vitals": {
      "blood_pressure_systolic": 118,
      "blood_pressure_diastolic": 76,
      "heart_rate": 72,
      "spo2": 98,
      "body_temperature": 36.7,
      "height_cm": 172,
      "weight_kg": 70.5,
      "bmi": 23.8,
      "respiratory_rate": 15
    }
  }'`;

    return (
        <section className="space-y-6" data-testid="kiosk-devices-tab">
            <div>
                <h2 className="font-display text-2xl font-semibold">Kiosk Devices</h2>
                <p className="text-sm text-gray-500 mt-1">Provision physical kiosks, rotate the device API key, and monitor live pushes.</p>
            </div>

            {/* API KEY CARD */}
            <div className="card-soft p-6" data-testid="kiosk-key-card">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-orange-50 grid place-items-center text-[var(--medx-orange)]">
                            <KeyRound className="w-5 h-5" />
                        </div>
                        <div>
                            <div className="text-xs font-mono tracking-[0.22em] text-gray-500">DEVICE API KEY</div>
                            <div className="font-display text-lg font-semibold">X-Kiosk-Key header</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className={`text-[10px] px-2 py-1 rounded-full font-mono tracking-wider ${keyInfo?.source === "rotated" ? "bg-emerald-50 text-emerald-700" : keyInfo?.source === "env" ? "bg-amber-50 text-amber-700" : "bg-gray-100 text-gray-500"}`}>
                            {(keyInfo?.source || "unset").toUpperCase()}
                        </span>
                        {keyInfo?.rotated_at && (
                            <span className="text-xs text-gray-500">Rotated {new Date(keyInfo.rotated_at).toLocaleString()}</span>
                        )}
                    </div>
                </div>

                <div className="mt-5 flex items-center gap-3 flex-wrap">
                    <code
                        className="flex-1 min-w-0 px-4 py-3 rounded-xl bg-gray-900 text-emerald-300 font-mono text-xs break-all"
                        data-testid="kiosk-key-value"
                    >
                        {loading ? "Loading…" : reveal ? (keyInfo?.api_key || "—") : (keyInfo?.masked || "—")}
                    </code>
                    <button
                        onClick={() => setReveal((v) => !v)}
                        className="btn-outline inline-flex items-center gap-2 text-sm"
                        data-testid="kiosk-key-reveal-toggle"
                    >
                        {reveal ? "Hide" : "Reveal"}
                    </button>
                    <button
                        onClick={() => copy(keyInfo?.api_key || "")}
                        disabled={!keyInfo?.api_key}
                        className="btn-outline inline-flex items-center gap-2 text-sm disabled:opacity-50"
                        data-testid="kiosk-key-copy"
                    >
                        <Copy className="w-4 h-4" /> Copy
                    </button>
                    <button
                        onClick={rotate}
                        disabled={rotating}
                        className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
                        data-testid="kiosk-key-rotate"
                    >
                        <RefreshCw className={`w-4 h-4 ${rotating ? "animate-spin" : ""}`} /> {rotating ? "Rotating…" : "Rotate Key"}
                    </button>
                </div>
            </div>

            {/* CURL EXAMPLE */}
            <div className="card-soft p-6" data-testid="kiosk-curl-card">
                <div className="flex items-center justify-between mb-3">
                    <div className="text-xs font-mono tracking-[0.22em] text-gray-500">EXAMPLE REQUEST (for hardware team)</div>
                    <button
                        onClick={() => copy(curlSnippet)}
                        className="btn-outline inline-flex items-center gap-2 text-xs"
                        data-testid="kiosk-curl-copy"
                    >
                        <Copy className="w-3.5 h-3.5" /> Copy curl
                    </button>
                </div>
                <pre className="bg-gray-900 text-gray-100 rounded-xl p-4 text-xs overflow-x-auto whitespace-pre font-mono">{curlSnippet}</pre>
            </div>

            {/* RECENT PUSHES */}
            <div className="card-soft overflow-hidden" data-testid="kiosk-pushes-feed">
                <div className="p-6 flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <div className="text-xs font-mono tracking-[0.22em] text-gray-500">RECENT PUSHES</div>
                        <div className="font-display text-lg font-semibold">Live kiosk feed</div>
                    </div>
                    <button
                        onClick={load}
                        className="btn-outline inline-flex items-center gap-2 text-xs"
                        data-testid="kiosk-pushes-refresh"
                    >
                        <RefreshCw className="w-3.5 h-3.5" /> Refresh
                    </button>
                </div>
                <table className="w-full text-sm">
                    <thead className="bg-orange-50/60 text-xs font-mono tracking-wider text-gray-500">
                        <tr>
                            <th className="text-left p-4">WHEN</th>
                            <th className="text-left p-4">DEVICE</th>
                            <th className="text-left p-4">PATIENT</th>
                            <th className="text-left p-4">SCORE</th>
                            <th className="text-left p-4">TRIAGE</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (<tr><td colSpan={5} className="p-8 text-center text-gray-500">Loading…</td></tr>)}
                        {!loading && pushes.length === 0 && (
                            <tr><td colSpan={5} className="p-10 text-center text-gray-500" data-testid="kiosk-pushes-empty">
                                No physical kiosk pushes yet. Once a device POSTs to <code className="font-mono text-xs">/api/kiosk/push</code> it will appear here.
                            </td></tr>
                        )}
                        {pushes.map((p) => (
                            <tr key={p.id} className="border-t border-gray-100 hover:bg-orange-50/30" data-testid={`kiosk-push-row-${p.id}`}>
                                <td className="p-4 text-gray-700 text-xs whitespace-nowrap">{p.captured_at ? new Date(p.captured_at).toLocaleString() : "—"}</td>
                                <td className="p-4 font-mono text-xs text-gray-700">{p.kiosk_id || "—"}</td>
                                <td className="p-4">
                                    <div className="font-medium text-gray-900">{p.patient_name}</div>
                                    <div className="text-xs text-gray-500 font-mono">{p.patient_email}</div>
                                </td>
                                <td className="p-4 font-display text-lg font-bold text-gray-900 tabular-nums">{p.health_score ?? "—"}</td>
                                <td className="p-4">
                                    {p.hr_flag ? (
                                        <span className={`text-[10px] px-2 py-1 rounded-full font-semibold ${
                                            p.hr_flag.code === "urgent_leave" ? "bg-red-50 text-red-700" :
                                            p.hr_flag.code === "take_break" ? "bg-amber-50 text-amber-700" :
                                            "bg-emerald-50 text-emerald-700"
                                        }`}>
                                            {p.hr_flag.label || p.hr_flag.code}
                                        </span>
                                    ) : <span className="text-xs text-gray-400">—</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

function ProfileChangesTab() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("pending");
    const [busy, setBusy] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/profile-changes?status_filter=${filter}`);
            setRows(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not load");
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [filter]);

    const decide = async (id, action) => {
        setBusy(id);
        const prev = rows;
        if (filter === "pending") setRows((r) => r.filter((x) => x.id !== id));
        try {
            await api.post(`/admin/profile-changes/${id}/${action}`);
            toast.success(action === "approve" ? "Change applied + patient notified." : "Request rejected.");
            await load();
        } catch (e) {
            setRows(prev);
            toast.error(formatApiError(e.response?.data?.detail) || `Could not ${action}`);
        } finally { setBusy(null); }
    };

    const label = { email: "Email", mobile: "Mobile", id_number: "ID Number", delete: "ACCOUNT DELETE" };
    return (
        <section className="space-y-6" data-testid="profile-changes-tab">
            <div>
                <h2 className="font-display text-2xl font-semibold">Profile Change Requests</h2>
                <p className="text-sm text-gray-500 mt-1">POPIA / HIPAA audit queue — patients request, OTP verifies, admin approves.</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
                {["pending", "approved", "rejected", "all"].map((s) => (
                    <button key={s} onClick={() => setFilter(s)}
                        className={`px-3 py-1.5 rounded-full text-xs font-mono tracking-wider ${filter === s ? "bg-[var(--medx-orange)] text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                        data-testid={`profile-changes-filter-${s}`}>
                        {s.toUpperCase()}
                    </button>
                ))}
            </div>
            <div className="card-soft overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-orange-50/60 text-xs font-mono tracking-wider text-gray-500">
                        <tr>
                            <th className="text-left p-4">WHEN</th>
                            <th className="text-left p-4">USER</th>
                            <th className="text-left p-4">TYPE</th>
                            <th className="text-left p-4">NEW VALUE</th>
                            <th className="text-left p-4">STATUS</th>
                            <th className="text-right p-4">ACTIONS</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (<tr><td colSpan={6} className="p-8 text-center text-gray-500">Loading…</td></tr>)}
                        {!loading && rows.length === 0 && (
                            <tr><td colSpan={6} className="p-10 text-center text-gray-500" data-testid="profile-changes-empty">No requests in this bucket.</td></tr>
                        )}
                        {rows.map((r) => (
                            <tr key={r.id} className="border-t border-gray-100 hover:bg-orange-50/30" data-testid={`profile-change-row-${r.id}`}>
                                <td className="p-4 text-xs text-gray-700 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                                <td className="p-4">
                                    <div className="font-medium text-gray-900">{r.user_name || "—"}</div>
                                    <div className="text-xs text-gray-500 font-mono">{r.user_email}</div>
                                </td>
                                <td className="p-4"><span className={`text-[10px] font-mono px-2 py-1 rounded-full ${r.change_type === "delete" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}>{label[r.change_type] || r.change_type}</span></td>
                                <td className="p-4 font-mono text-xs text-gray-700 break-all max-w-xs">{r.new_value || "—"}</td>
                                <td className="p-4">
                                    <span className={`text-[10px] font-mono px-2 py-1 rounded-full ${
                                        r.status === "pending_otp" ? "bg-amber-50 text-amber-700" :
                                        r.status === "pending_admin" ? "bg-blue-50 text-blue-700" :
                                        r.status === "approved" ? "bg-emerald-50 text-emerald-700" :
                                        "bg-gray-100 text-gray-500"}`}>{(r.status || "").toUpperCase()}</span>
                                </td>
                                <td className="p-4 text-right whitespace-nowrap">
                                    {(r.status === "pending_admin" || r.status === "pending_otp") ? (
                                        <>
                                            <button onClick={() => decide(r.id, "approve")} disabled={busy === r.id || r.status === "pending_otp"}
                                                title={r.status === "pending_otp" ? "Patient hasn't verified OTP yet" : ""}
                                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-40 mr-2"
                                                data-testid={`profile-change-approve-${r.id}`}>
                                                <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                                            </button>
                                            <button onClick={() => decide(r.id, "reject")} disabled={busy === r.id}
                                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-gray-200 text-gray-700 text-xs font-semibold hover:bg-gray-50"
                                                data-testid={`profile-change-reject-${r.id}`}>
                                                <XCircle className="w-3.5 h-3.5" /> Reject
                                            </button>
                                        </>
                                    ) : (<span className="text-xs text-gray-400">{r.decided_by || "—"}</span>)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

function ScanReviewsTab() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("pending");
    const [busy, setBusy] = useState(null);
    const [viewing, setViewing] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/scans?status_filter=${filter}`);
            setRows(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not load");
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [filter]);

    const decide = async (id, action) => {
        if (action === "approve" && !window.confirm("Approve this scan? We'll OCR the slip via Gemini and append the extracted vitals to the patient's dashboard.")) return;
        setBusy(id);
        const prev = rows;
        if (filter === "pending") setRows((r) => r.filter((x) => x.id !== id));
        try {
            const { data } = await api.post(`/admin/scans/${id}/${action}`);
            if (action === "approve") toast.success(`Scan processed. Health Score: ${data?.vitals?.health_score ?? "–"}`, { duration: 6000 });
            else toast.success("Scan rejected.");
            await load();
            if (viewing?.id === id) setViewing(null);
        } catch (e) {
            setRows(prev);
            toast.error(formatApiError(e.response?.data?.detail) || `Could not ${action}`);
        } finally { setBusy(null); }
    };

    const view = async (r) => {
        try {
            const { data } = await api.get(`/admin/scans/${r.id}`);
            setViewing(data);
        } catch (e) { toast.error("Could not load image"); }
    };

    return (
        <section className="space-y-6" data-testid="scan-reviews-tab">
            <div>
                <h2 className="font-display text-2xl font-semibold">Patient Scan Uploads</h2>
                <p className="text-sm text-gray-500 mt-1">Approve to run Gemini OCR and auto-create a vitals session for the patient.</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
                {["pending", "processed", "rejected", "all"].map((s) => (
                    <button key={s} onClick={() => setFilter(s)}
                        className={`px-3 py-1.5 rounded-full text-xs font-mono tracking-wider ${filter === s ? "bg-[var(--medx-orange)] text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                        data-testid={`scan-reviews-filter-${s}`}>
                        {s.toUpperCase()}
                    </button>
                ))}
            </div>
            <div className="card-soft overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-orange-50/60 text-xs font-mono tracking-wider text-gray-500">
                        <tr>
                            <th className="text-left p-4">WHEN</th>
                            <th className="text-left p-4">PATIENT</th>
                            <th className="text-left p-4">NOTES</th>
                            <th className="text-left p-4">STATUS</th>
                            <th className="text-right p-4">ACTIONS</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (<tr><td colSpan={5} className="p-8 text-center text-gray-500">Loading…</td></tr>)}
                        {!loading && rows.length === 0 && (
                            <tr><td colSpan={5} className="p-10 text-center text-gray-500" data-testid="scan-reviews-empty">No scans in this bucket.</td></tr>
                        )}
                        {rows.map((r) => (
                            <tr key={r.id} className="border-t border-gray-100 hover:bg-orange-50/30" data-testid={`scan-review-row-${r.id}`}>
                                <td className="p-4 text-xs text-gray-700 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                                <td className="p-4">
                                    <div className="font-medium text-gray-900">{r.user_name || "—"}</div>
                                    <div className="text-xs text-gray-500 font-mono">{r.user_email}</div>
                                </td>
                                <td className="p-4 text-xs text-gray-700 max-w-xs">{r.notes || "—"}</td>
                                <td className="p-4">
                                    <span className={`text-[10px] font-mono px-2 py-1 rounded-full ${
                                        r.status === "pending" ? "bg-amber-50 text-amber-700" :
                                        r.status === "processed" ? "bg-emerald-50 text-emerald-700" :
                                        "bg-gray-100 text-gray-500"}`}>{(r.status || "").toUpperCase()}</span>
                                </td>
                                <td className="p-4 text-right whitespace-nowrap">
                                    <button onClick={() => view(r)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-gray-200 text-gray-700 text-xs font-semibold hover:bg-gray-50 mr-2" data-testid={`scan-review-view-${r.id}`}>
                                        <FileImage className="w-3.5 h-3.5" /> View
                                    </button>
                                    {r.status === "pending" && (
                                        <>
                                            <button onClick={() => decide(r.id, "approve")} disabled={busy === r.id}
                                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-40 mr-2"
                                                data-testid={`scan-review-approve-${r.id}`}>
                                                <CheckCircle2 className="w-3.5 h-3.5" /> Approve + OCR
                                            </button>
                                            <button onClick={() => decide(r.id, "reject")} disabled={busy === r.id}
                                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-gray-200 text-gray-700 text-xs font-semibold hover:bg-gray-50"
                                                data-testid={`scan-review-reject-${r.id}`}>
                                                <XCircle className="w-3.5 h-3.5" /> Reject
                                            </button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {viewing && (
                <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setViewing(null)} data-testid="scan-review-modal">
                    <div onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
                        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                            <div>
                                <h3 className="font-display text-lg font-semibold">{viewing.user_name}'s scan</h3>
                                <div className="text-xs text-gray-500 font-mono">{viewing.user_email} · {new Date(viewing.created_at).toLocaleString()}</div>
                            </div>
                            <button onClick={() => setViewing(null)} className="w-9 h-9 rounded-full hover:bg-gray-100 grid place-items-center text-gray-500"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="p-5 overflow-y-auto">
                            <img src={`data:${viewing.mime_type || "image/jpeg"};base64,${viewing.image_base64}`}
                                alt="Scan" className="max-h-[60vh] mx-auto rounded-xl shadow" data-testid="scan-review-image" />
                            {viewing.notes && <div className="mt-4 text-sm text-gray-700"><b>Note:</b> {viewing.notes}</div>}
                            {viewing.ocr_vitals && (
                                <pre className="mt-4 bg-gray-900 text-emerald-300 p-4 rounded-xl text-xs overflow-x-auto">{JSON.stringify(viewing.ocr_vitals, null, 2)}</pre>
                            )}
                        </div>
                        {viewing.status === "pending" && (
                            <div className="p-4 border-t border-gray-100 flex justify-end gap-2">
                                <button onClick={() => decide(viewing.id, "reject")} className="btn-outline text-sm">
                                    <XCircle className="w-4 h-4 inline mr-1" /> Reject
                                </button>
                                <button onClick={() => decide(viewing.id, "approve")} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700" data-testid="scan-review-modal-approve">
                                    <CheckCircle2 className="w-4 h-4" /> Approve + run OCR
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
}



/* ================================================================
 * DependentsTab — admin review queue for Family-Account dependents.
 * ================================================================ */
function DependentsTab() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("pending");
    const [busy, setBusy] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/dependents?status_filter=${filter}`);
            setRows(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not load");
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [filter]);

    const decide = async (id, action) => {
        setBusy(id);
        try {
            if (action === "reject") {
                const reason = window.prompt("Reason for rejection (shown to the parent):", "Could not verify dependent details.") || "";
                await api.post(`/admin/dependents/${id}/reject`, { reason });
                toast.success("Dependent rejected.");
            } else {
                await api.post(`/admin/dependents/${id}/approve`);
                toast.success("Dependent approved. Parent can now switch to this profile.");
            }
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || `Could not ${action}`);
        } finally { setBusy(null); }
    };

    return (
        <section className="space-y-6" data-testid="dependents-tab">
            <div>
                <h2 className="font-display text-2xl font-semibold">Family Dependents</h2>
                <p className="text-sm text-gray-500 mt-1">Review child / spouse / elderly-parent profiles submitted by patients. Approve to grant them a dependent dashboard.</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
                {["pending", "approved", "rejected", "all"].map((s) => (
                    <button key={s} onClick={() => setFilter(s)}
                        className={`px-3 py-1.5 rounded-full text-xs font-mono tracking-wider ${filter === s ? "bg-[var(--medx-orange)] text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                        data-testid={`dependents-filter-${s}`}>
                        {s.toUpperCase()}
                    </button>
                ))}
            </div>
            <div className="card-soft overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-orange-50/60 text-xs font-mono tracking-wider text-gray-500">
                        <tr>
                            <th className="text-left p-4">WHEN</th>
                            <th className="text-left p-4">DEPENDENT</th>
                            <th className="text-left p-4">RELATIONSHIP</th>
                            <th className="text-left p-4">DOB · AGE</th>
                            <th className="text-left p-4">ID</th>
                            <th className="text-left p-4">STATUS</th>
                            <th className="text-right p-4">ACTIONS</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (<tr><td colSpan={7} className="p-8 text-center text-gray-500">Loading…</td></tr>)}
                        {!loading && rows.length === 0 && (
                            <tr><td colSpan={7} className="p-10 text-center text-gray-500" data-testid="dependents-empty">No dependents in this bucket.</td></tr>
                        )}
                        {rows.map((r) => (
                            <tr key={r.id} className="border-t border-gray-100 hover:bg-orange-50/30" data-testid={`dependent-row-${r.id}`}>
                                <td className="p-4 text-xs text-gray-700 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                                <td className="p-4">
                                    <div className="font-medium text-gray-900">{r.name}</div>
                                    <div className="text-xs text-gray-500">Parent · <span className="font-mono">{r.parent_id?.slice(0, 8)}…</span></div>
                                </td>
                                <td className="p-4">
                                    <span className="text-[10px] font-mono px-2 py-1 rounded-full bg-blue-50 text-blue-700 capitalize">
                                        {r.relationship}
                                    </span>
                                </td>
                                <td className="p-4 text-xs text-gray-700">
                                    <div>{r.date_of_birth}</div>
                                    <div className="text-gray-500">age {r.age}{r.gender ? ` · ${r.gender}` : ""}</div>
                                </td>
                                <td className="p-4 text-xs text-gray-700 font-mono break-all max-w-xs">{r.id_number || "—"}</td>
                                <td className="p-4">
                                    <span className={`text-[10px] font-mono px-2 py-1 rounded-full ${
                                        r.status === "pending" ? "bg-amber-50 text-amber-700" :
                                        r.status === "approved" ? "bg-emerald-50 text-emerald-700" :
                                        "bg-gray-100 text-gray-500"}`}>{(r.status || "").toUpperCase()}</span>
                                </td>
                                <td className="p-4 text-right whitespace-nowrap">
                                    {r.status === "pending" ? (
                                        <>
                                            <button onClick={() => decide(r.id, "approve")} disabled={busy === r.id}
                                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-40 mr-2"
                                                data-testid={`dependent-approve-${r.id}`}>
                                                <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                                            </button>
                                            <button onClick={() => decide(r.id, "reject")} disabled={busy === r.id}
                                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-gray-200 text-gray-700 text-xs font-semibold hover:bg-gray-50 disabled:opacity-40"
                                                data-testid={`dependent-reject-${r.id}`}>
                                                <XCircle className="w-3.5 h-3.5" /> Reject
                                            </button>
                                        </>
                                    ) : (
                                        <span className="text-xs text-gray-400">{r.decided_at ? new Date(r.decided_at).toLocaleString() : "—"}</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
}
